import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Visibility } from "@wodiary/shared";
import { api, ApiError } from "../api/client.js";
import { usePairing, useSettings } from "../api/queries.js";
import { useAuth } from "../auth/AuthContext.js";
import { errorMessage } from "../lib/errors.js";
import { daysTogether, formatDateTime } from "../lib/format.js";
import { NavBar } from "../components/NavBar.js";
import { Avatar } from "../components/Avatar.js";
import { Loading, QueryError } from "../components/AsyncState.js";
import { Modal } from "../components/Modal.js";
import { useToast } from "../components/ToastContext.js";
import {
  ChevronRightIcon,
  DeviceIcon,
  DownloadIcon,
  KeyIcon,
  UserIcon,
} from "../components/icons.js";

/**
 * 设置（spec §5.8 `SET-1`）。
 *
 * 这里**没有「解除配对」按钮**（spec §5.9：解绑只走运维命令）。也没有通知、
 * 没有 PDF 导出 —— 原型里那几项在 PRD 阶段就被砍掉了。
 *
 * 可见性概览只统计**自己的**篇目（后端 `countMine`），不含任何对对方的推断：
 * 连「TA 有几篇共享」都不显示，因为那可以从差分里反推私密篇目量。
 */
export function SettingsPage() {
  const { user, logout } = useAuth();
  const settings = useSettings();
  const pairing = usePairing();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [nicknameOpen, setNicknameOpen] = useState(false);

  const updateNickname = useMutation({
    mutationFn: (username: string) => api.updateProfile({ username }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      setNicknameOpen(false);
      toast.show("已保存");
    },
  });

  const updateVisibility = useMutation({
    mutationFn: (v: Visibility) => api.updateProfile({ default_visibility: v }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
      toast.show("已更新新日记的默认可见范围");
    },
    onError: (e) => toast.show(errorMessage(e)),
  });

  if (user === null) return null;

  const profile = settings.data?.profile;
  const counts = settings.data?.visibility;
  const active = pairing.data?.state === "active";
  const partner = profile?.partner_username ?? pairing.data?.partnerUsername ?? "TA";
  const days = daysTogether(profile?.paired_at);

  return (
    <>
      <NavBar title="设置" />
      <div className="page">
        {settings.isLoading && <Loading />}
        {settings.isError && (
          <QueryError
            error={settings.error}
            onRetry={() => void settings.refetch()}
          />
        )}

        {profile !== undefined && (
          <>
            {/* 配对状态 */}
            <div className="pair-card">
              <div className="dual-ava">
                <Avatar name={profile.username} variant="me" size={40} />
                {active && <Avatar name={partner} variant="ta" size={40} />}
              </div>
              <div className="pair-info">
                <span className="l1">
                  {active ? `我 与 ${partner}` : `我是 ${profile.username}`}
                </span>
                <span className="l2">
                  {active
                    ? `已配对 ${days ?? 1} 天 · 双向授权中`
                    : "还没有配对"}
                </span>
              </div>
              {!active && (
                <Link
                  to="/pairing"
                  className="chip"
                  style={{
                    color: "#fff",
                    borderColor: "var(--stone)",
                    background: "var(--primary)",
                    height: 34,
                    padding: "0 16px",
                    fontWeight: 600,
                  }}
                >
                  去配对
                </Link>
              )}
            </div>

            {/* 可见性概览 —— 只有自己的 */}
            <div className="matrix">
              <div className="mx-head">
                <span className="c">我写的</span>
                <span className="c">{partner} 写的</span>
              </div>
              <div className="mx-row">
                <span className="nm">私密日记</span>
                <span className="mx-cell mine">我可见</span>
                <span className="mx-cell">不可见</span>
              </div>
              <div className="mx-row">
                <span className="nm">共享日记</span>
                <span className="mx-cell mine">我可见</span>
                <span className="mx-cell ta">{partner} 可见</span>
              </div>
              <div className="mx-row">
                <span className="nm" style={{ color: "var(--steel)" }}>
                  我目前有 {counts?.private ?? 0} 篇私密 · {counts?.shared ?? 0} 篇共享
                </span>
              </div>
            </div>

            {/* 默认可见范围 */}
            <section className="card stack-sm">
              <div className="section-title">新日记默认可见范围</div>
              <p className="small ghost-text">
                写新日记时的初始选择，每次仍然可以改。
              </p>
              <div className="chip-row">
                <DefaultVisibilityChip
                  value="shared"
                  current={profile.default_visibility}
                  label={`共享给 ${partner}`}
                  onSelect={(v) => updateVisibility.mutate(v)}
                />
                <DefaultVisibilityChip
                  value="private"
                  current={profile.default_visibility}
                  label="只写给自己"
                  onSelect={(v) => updateVisibility.mutate(v)}
                />
              </div>
            </section>

            {/* 导出 */}
            <div className="list-card">
              <Link to="/export" className="setting-row">
                <span className="set-ico sky">
                  <DownloadIcon size={17} />
                </span>
                <span className="set-txt">
                  <span className="t">导出日记本</span>
                  <span className="d">
                    {settings.data?.last_exported_at != null
                      ? `上次导出 ${formatDateTime(settings.data.last_exported_at)}`
                      : "还没有导出过 —— 建议现在就导出一次"}
                  </span>
                </span>
                <ChevronRightIcon size={16} style={{ color: "var(--stone)" }} />
              </Link>
            </div>

            {/* 账号 */}
            <div className="list-card">
              <button
                type="button"
                className="setting-row"
                onClick={() => setNicknameOpen(true)}
              >
                <span className="set-ico lav">
                  <UserIcon size={17} />
                </span>
                <span className="set-txt">
                  <span className="t">昵称</span>
                  <span className="d">历史日记会实时显示当前昵称</span>
                </span>
                <span className="set-val">{profile.username}</span>
                <ChevronRightIcon size={16} style={{ color: "var(--stone)" }} />
              </button>

              <Link to="/settings/password" className="setting-row">
                <span className="set-ico mint">
                  <KeyIcon size={17} />
                </span>
                <span className="set-txt">
                  <span className="t">修改密码</span>
                  <span className="d">需要输入当前密码</span>
                </span>
                <ChevronRightIcon size={16} style={{ color: "var(--stone)" }} />
              </Link>

              <Link to="/settings/sessions" className="setting-row">
                <span className="set-ico rose">
                  <DeviceIcon size={17} />
                </span>
                <span className="set-txt">
                  <span className="t">登录的设备</span>
                  <span className="d">手机丢了可以只登出其它设备</span>
                </span>
                <ChevronRightIcon size={16} style={{ color: "var(--stone)" }} />
              </Link>
            </div>

            <button
              type="button"
              className="btn btn-ghost btn-block"
              onClick={() => void logout()}
            >
              退出当前登录
            </button>

            <p className="foot-note">
              想解除配对、退出账号或重置密码？这些只走服务器命令行，应用内没有入口
              —— 先执行一次导出，再在服务器上运行 `pnpm ops`。
            </p>
          </>
        )}
      </div>

      {nicknameOpen && profile !== undefined && (
        <NicknameModal
          current={profile.username}
          busy={updateNickname.isPending}
          error={
            updateNickname.isError &&
            updateNickname.error instanceof ApiError &&
            updateNickname.error.code === "username_taken"
              ? "这个名字已经有人用了。"
              : updateNickname.isError
                ? errorMessage(updateNickname.error)
                : null
          }
          onCancel={() => {
            updateNickname.reset();
            setNicknameOpen(false);
          }}
          onSave={(username) => updateNickname.mutate(username)}
        />
      )}
    </>
  );
}

function DefaultVisibilityChip({
  value,
  current,
  label,
  onSelect,
}: {
  value: Visibility;
  current: Visibility;
  label: string;
  onSelect: (v: Visibility) => void;
}) {
  return (
    <button
      type="button"
      className={current === value ? "chip on" : "chip"}
      onClick={() => {
        if (current !== value) onSelect(value);
      }}
    >
      {label}
    </button>
  );
}

function NicknameModal({
  current,
  busy,
  error,
  onCancel,
  onSave,
}: {
  current: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (username: string) => void;
}) {
  const [value, setValue] = useState(current);
  return (
    <Modal title="修改昵称" onClose={onCancel} closeOnMask={!busy}>
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          const name = value.trim();
          if (name !== "" && name !== current) onSave(name);
        }}
      >
        {error !== null && <div className="banner banner-error">{error}</div>}
        <label className="field-block">
          <span className="field-label">昵称</span>
          <span className="field">
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              maxLength={20}
              autoFocus
            />
          </span>
          <span className="field-hint">
            1–20 个字符，中文和 emoji 都可以。历史日记会用新昵称显示。
          </span>
        </label>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            取消
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || value.trim() === "" || value.trim() === current}
          >
            {busy ? "保存中…" : "保存"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
